import React, { useMemo } from 'react';
import {
  LedgerHeader,
  ledgerContainerClass,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { UserListItem } from '../molecules/UserListItem';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';
import type { UserStatus } from '../../api/userManagementApi';

export interface UserItem {
  id: string;
  fullName: string;
  email: string;
  joinedAt: string;
  roles: string[];
  status?: UserStatus;
}

export interface UsersListViewProps {
  users: UserItem[];
  loading: boolean;
  onItemClick?: (id: string) => void;
}

const USERS_COLLAPSE: LedgerCollapse = {
  container: 'users-collection',
  at: 620,
};

const USERS_COLUMNS: LedgerColumn[] = [
  { key: 'role', label: 'Role', flex: 1 },
  { key: 'status', label: 'Status', flex: 0.9 },
  { key: 'joined', label: 'Joined', flex: 0.9, hideBelow: 760 },
];

export const UsersListView: React.FC<UsersListViewProps> = ({
  users,
  loading,
  onItemClick,
}) => {
  const { activeUsers, offboardedUsers } = useMemo(() => {
    const active: UserItem[] = [];
    const offboarded: UserItem[] = [];
    for (const user of users) {
      if (user.status === 'Inactive') {
        offboarded.push(user);
      } else {
        active.push(user);
      }
    }
    return { activeUsers: active, offboardedUsers: offboarded };
  }, [users]);

  const renderUser = (user: UserItem) => (
    <UserListItem
      key={user.id}
      user={user}
      columns={USERS_COLUMNS}
      collapse={USERS_COLLAPSE}
      onRowClick={onItemClick}
    />
  );

  return (
    <div className={ledgerContainerClass(USERS_COLLAPSE.container)}>
      <ListViewShell
        itemCount={users.length}
        loading={loading}
        emptyMessage="No users found"
        data-testid="users-table"
      >
        <LedgerHeader
          columns={USERS_COLUMNS}
          collapse={USERS_COLLAPSE}
          titleLabel="Name"
          showValues={false}
          showActions
          hideBelow={USERS_COLLAPSE.at}
        />
        {activeUsers.map(renderUser)}
        {offboardedUsers.length > 0 && (
          <>
            <div className="flex items-center gap-2 px-4 py-1.5">
              <span className="type-caption font-semibold tracking-[0.5px] text-content-secondary uppercase">
                Offboarded
              </span>
            </div>
            {offboardedUsers.map(renderUser)}
          </>
        )}
      </ListViewShell>
    </div>
  );
};
